import {
  Box,
  Button,
  Grid,
  styled,
  TextField,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { AddressFinderForm } from "../../../../components/AddressFinderForm";
import { PersonalDetailsForm } from "../../../../components/employees/PersonalDetailsForm";
import { Employee } from "../../../../models";
import { useData } from "../../../../utils/useData";
import { useDynamicEmployeeValidationSchema } from "../../../../utils/useDynamicValidationSchema";
import { SectionTitle } from "../SectionTitle";
import { useUpdateUser } from "../../../../hooks/users/useUpdateUser";

export type ReplaceEmployeeFormValuesType = {
  customerEmployeeId: string;
  firstName: string;
  lastName: string;
  cardDisplayName: string | null;
  contact: {
    email: string;
    phone: string;
    address: {
      address1: string;
      address2: string;
      // address3: string;
      // address4: string;
      // address5: string;
      city: string;
      stateOrProvince: string;
      postalCode: string;
      country: string;
    };
  };
};

const SaveButton = styled(Button)({
  marginTop: 12
});

const EmployeePersonalDetails = ({ employee }: { employee: Employee }) => {
  const { allEmployees, employer } = useData();
  const [error, setError] = useState<string | null>(null);

  const updateUser = useUpdateUser({
    onError: (error: Error) => {
      setError(error.message);
    }
  })

  const validationSchema = useDynamicEmployeeValidationSchema(
    employer.data?.country.id ?? "",
    allEmployees.data?.items,
    employee.id
  );

  const { resetForm, ...formik } = useFormik({
    initialValues: {
      customerEmployeeId: employee?.customerEmployeeId ?? "",
      firstName: employee.firstName,
      lastName: employee.lastName,
      cardDisplayName: employee?.cardDisplayName ?? null,
      contact: {
        email: employee.contact.email,
        phone: employee.contact.phone,
        address: {
          address1: employee.contact.address?.address1 ?? "",
          address2: employee.contact.address?.address2 ?? "",
          city: employee.contact.address?.city ?? "",
          stateOrProvince: employee.contact.address?.stateOrProvince ?? "",
          postalCode: employee.contact.address?.postalCode ?? "",
          country: employer.data?.country.id ?? ""
        }
      }
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      updateUser.mutate({values, employee});
    },
    enableReinitialize: true
  });

  return (
    <Grid container item spacing={2} xs={7}>
      <Grid item xs={12}>
        <SectionTitle>Employee</SectionTitle>
      </Grid>
      <PersonalDetailsForm formik={formik} allowEmailChange={true} />
      <Grid item xs={12} md={6}>
        <TextField
          value={formik.values.cardDisplayName}
          onChange={formik.handleChange}
          label="Name to display on card (Max. 26 Characters)"
          name="cardDisplayName"
          fullWidth
          size="small"
          error={
            formik.touched.cardDisplayName &&
            Boolean(formik.errors.cardDisplayName)
          }
          helperText={
            formik.touched.cardDisplayName && formik.errors.cardDisplayName
          }
        />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>Postal Address</SectionTitle>
      </Grid>
      <Box
        width="100%"
        pt={1}
        ml={2}
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <AddressFinderForm
          formik={formik}
          promptEmployerAddress={true}
          required={formik.values.contact.address.address1.length > 0} // Don't mark address fields required if user is not filling in address. This is as an employer might want to edit an employee, before the employee has entered their address (and the employer might not want to fill it in). The validation of this is handled in the useDynamicEmployeeValidationSchema, this is just to mark the fields visually.
        />

        <SaveButton variant="contained" onClick={formik.submitForm} fullWidth>
          Save
        </SaveButton>
      </Box>
      {error && (
        <Typography color="error" my={2} ml={2}>
          {error}
        </Typography>
      )}
    </Grid>
  );
};

export { EmployeePersonalDetails };
